import { cn } from '@/shared/lib/utils'

interface LogoMarkProps {
  className?: string
  size?: number
}

export function LogoMark({ className, size = 28 }: LogoMarkProps) {
  return (
    <svg
      width={size}
      height={size * (71.09 / 81.82)}
      viewBox="0 0 81.82 71.09"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M31.94,1.74L1.71,32.36c-2.28,2.31-2.28,6.07,0,8.38l28.52,28.89c2.28,2.31,5.99,2.31,8.27,0l30.22-30.62c2.28-2.31,2.28-6.07,0-8.38L40.21,1.74c-2.28-2.31-5.99-2.31-8.27,0Z"
        fill="rgba(255, 212, 103, 0.69)"
      />
      <path
        d="M55.95,2.36l-37.9,13.98c-3.04,1.12-4.6,4.52-3.5,7.6l15.49,43.11c1.1,3.08,4.46,4.66,7.5,3.54l37.9-13.98c3.04-1.12,4.6-4.52,3.5-7.6L63.45,5.9c-1.11-3.08-4.46-4.66-7.5-3.54Z"
        fill="rgba(129, 180, 87, 0.6)"
      />
      <path
        d="M75.97,13h-39.78c-3.23,0-5.85,2.65-5.85,5.93v46.23c0,3.27,2.62,5.93,5.85,5.93h39.78c3.23,0,5.85-2.65,5.85-5.93V18.93c0-3.27-2.62-5.93-5.85-5.93Z"
        fill="rgba(6, 90, 73, 0.5)"
      />
    </svg>
  )
}

interface LogoProps {
  className?: string
  size?: 'sm' | 'md' | 'lg'
  showText?: boolean
  variant?: 'default' | 'white'
}

// Height of the full mark+wordmark lockup per size. The wordmark occupies
// ~70% of the image height, so these are taller than the old mark-only
// sizes to keep "iWarranty" legible (roughly matching the previous 18/22px
// wordmark) rather than shrinking with the mark.
const lockupHeight = {
  sm: 'h-[28px]',
  md: 'h-[34px]',
  lg: 'h-[42px]',
}

// Matching mark-only sizes (used when the wordmark is suppressed).
const markSize = {
  sm: 22,
  md: 28,
  lg: 36,
}

export function Logo({ className, size = 'md', showText = true }: LogoProps) {
  // Mark-only: fall back to the SVG mark (this is also the app/favicon icon).
  if (!showText) {
    return <LogoMark size={markSize[size]} className={className} />
  }

  // Full lockup (mark + "iWarranty" wordmark) as a single flat image,
  // rendered as-is on every surface (no colour inversion).
  //
  // `max-w-none` + `shrink-0` are load-bearing: Tailwind's preflight applies
  // `img { max-width: 100% }`, and inside a shrink-wrapped flex parent (the
  // toolbar's logo button) that 100% resolves against a parent whose own width
  // depends on this image - a circular constraint that collapses the width to
  // 0, so the logo silently disappears even though the file loads fine. Pinning
  // the height and letting the width follow the intrinsic aspect ratio (with no
  // max-width clamp and no flex shrink) keeps the lockup visible on every
  // surface, flex parent or not.
  return (
    <img
      src="/iwarranty-logo.png"
      alt="iWarranty"
      className={cn(
        'block w-auto max-w-none shrink-0 object-contain',
        lockupHeight[size],
        className,
      )}
    />
  )
}
